import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { API } from "../lib/api";
import useSeo from "../hooks/useSeo";
import JobCard from "../components/JobCard";
import StateSelect from "../components/StateSelect";
import { fieldCls } from "../components/QuickMatchForm";

const PAGE = 24;
const pill = (on) => `shrink-0 h-10 rounded-full px-4 text-sm font-semibold ring-1 transition-colors ${on ? "bg-[#0F2A1F] text-white ring-[#0F2A1F]" : "bg-white text-[#0F2A1F] ring-[#0F2A1F]/15 hover:ring-[#0F2A1F]/40"}`;

export default function Jobs() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const stateCode = params.get("state") || "";
  const [search, setSearch] = useState(params.get("q") || "");
  const [q, setQ] = useState(search);
  const [cats, setCats] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const catName = cats.find((c) => c.key === active)?.label;

  useSeo({
    title: active === "all" ? "Browse Remote Jobs in All 50 States · Direct Hire Team" : `Remote ${catName || active} Jobs · Direct Hire Team`,
    description: "Browse remote roles across payroll, finance, HR, IT, marketing, sales, legal, admin and operations in all 50 states. Apply in 60 seconds — free.",
    url: `https://directhireteam.com/jobs${active !== "all" ? `?category=${active}` : ""}`,
  });

  useEffect(() => {
    fetch(`${API}/categories${stateCode ? `?state=${stateCode}` : ""}`).then((r) => r.json()).then((d) => setCats(d.categories || [])).catch(() => {});
  }, [stateCode]);

  useEffect(() => { const t = setTimeout(() => setQ(search), 300); return () => clearTimeout(t); }, [search]);

  useEffect(() => {
    if ((params.get("q") || "") === q.trim()) return;
    const n = new URLSearchParams(params);
    if (q.trim()) n.set("q", q.trim()); else n.delete("q");
    setParams(n, { replace: true });
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = (skip) => {
    const p = new URLSearchParams({ skip, limit: PAGE });
    if (active !== "all") p.set("category", active);
    if (stateCode) p.set("state", stateCode);
    if (q.trim()) p.set("q", q.trim());
    setLoading(true);
    return fetch(`${API}/jobs?${p}`).then((r) => { setTotal(Number(r.headers.get("X-Total-Count")) || 0); return r.json(); })
      .then((rows) => setJobs((prev) => (skip ? [...prev, ...rows] : rows || []))).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(0); }, [active, stateCode, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k, v) => { const n = new URLSearchParams(params); if (v && v !== "all") n.set(k, v); else n.delete(k); setParams(n); };
  const catLabel = (k) => cats.find((c) => c.key === k)?.label;

  return (
    <div className="bg-[#FAF7EE] min-h-screen pt-28 md:pt-32 pb-24" data-testid="jobs-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#0d7a55] font-bold">{total > 0 ? `${total} open roles` : "Open roles"} · 100% remote</div>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2A1F] leading-tight max-w-3xl">Find a remote job you'll actually want.</h1>
        <p className="mt-3 text-base md:text-lg text-[#0F2A1F]/65 max-w-2xl">Filter by field and state, then apply in 60 seconds. Free for job seekers — always.</p>

        <div className="mt-8 grid sm:grid-cols-[1fr_260px] gap-3">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0F2A1F]/40" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Job title or keyword…" className={`${fieldCls} bg-white pl-10`} data-testid="jobs-search-input" />
          </div>
          <StateSelect value={stateCode} onChange={(v) => update("state", v)} placeholder="All 50 states" className={`${fieldCls} bg-white`} testId="jobs-state-select" />
        </div>

        <div className="mt-4 -mx-5 px-5 md:mx-0 md:px-0 flex md:flex-wrap gap-2 overflow-x-auto no-scrollbar" data-testid="category-filters">
          <button onClick={() => update("category", "all")} className={pill(active === "all")} data-testid="filter-all">All roles</button>
          {cats.map((c) => (
            <button key={c.key} onClick={() => update("category", c.key)} className={pill(active === c.key)} data-testid={`filter-${c.key}`}>
              {c.label} <span className="opacity-60 ml-1">{c.count}</span>
            </button>
          ))}
        </div>

        <button onClick={() => nav("/", { state: { focusMatch: true } })} className="mt-6 w-full text-left rounded-2xl bg-[#0F2A1F] text-white p-4 md:p-5 flex items-center gap-4 hover:bg-[#163b2c] transition-colors" data-testid="jobs-quick-match-banner">
          <Sparkles className="h-6 w-6 shrink-0 text-[#D4AF37]" />
          <div className="flex-1 min-w-0">
            <div className="font-bold">Don't want to scroll? Let a recruiter match you.</div>
            <div className="text-sm text-white/65">One 30-second form — we'll find the right role for you.</div>
          </div>
          <ArrowRight className="h-5 w-5 shrink-0 text-[#D4AF37]" />
        </button>

        <div className="mt-6 text-sm text-[#0F2A1F]/60" data-testid="jobs-count">{loading && !jobs.length ? "Loading…" : `Showing ${jobs.length} of ${total} ${total === 1 ? "role" : "roles"}`}</div>
        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="jobs-grid">
          {jobs.map((j) => <JobCard key={j.id} job={j} catLabel={catLabel(j.category)} cardTestId={`job-card-${j.id}`} applyTestId={`apply-${j.id}`} />)}
        </div>

        {jobs.length < total && (
          <div className="mt-10 flex justify-center">
            <button onClick={() => load(jobs.length)} disabled={loading} className="h-12 px-8 rounded-xl bg-white ring-1 ring-[#0F2A1F]/20 text-[#0F2A1F] font-semibold inline-flex items-center gap-2 hover:ring-[#0F2A1F]/50 transition-shadow disabled:opacity-60" data-testid="jobs-load-more">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Show more roles
            </button>
          </div>
        )}

        {!loading && jobs.length === 0 && (
          <div className="mt-10 text-center rounded-2xl bg-white p-10 ring-1 ring-[#0F2A1F]/10" data-testid="jobs-empty">
            <div className="text-xl font-bold text-[#0F2A1F]">No roles match that search — yet.</div>
            <p className="mt-2 text-[#0F2A1F]/60">Try another keyword, or let a recruiter find one for you.</p>
            <button onClick={() => nav("/", { state: { focusMatch: true } })} className="mt-5 h-12 px-6 rounded-xl bg-[#D4AF37] text-[#0F2A1F] font-bold" data-testid="jobs-empty-match">Get matched instead</button>
          </div>
        )}
      </div>
    </div>
  );
}
